import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Snackbar,
  SnackbarContent,
  IconButton,
} from '@material-ui/core';
import { CheckCircle, Error, Info, Close, Warning } from '@material-ui/icons';
import { amber, green } from '@material-ui/core/colors';

export interface ButtonWithSnackBarProps {
  buttonClassName?: string;
  buttonVariant?: 'text' | 'outlined' | 'contained' | undefined;
  buttonSize?: 'small' | 'medium' | 'large' | undefined;
  buttonColor?: 'inherit' | 'primary' | 'secondary' | 'default' | undefined;
  buttonIcon?: any;
  buttonTitle?: string;
  buttonLabel?: string;
  buttonStyle?: any;
  hideSnack?: boolean;
  snackBarMessage: string;
  variant: 'success' | 'error' | 'info' | 'warning';
  onSubmit: () => void;
}

export interface MySnackbarContentWrapperProps {
  variant: 'success' | 'error' | 'info' | 'warning';
  message: string;
  other?: any;
  className?: any;
  onClose: () => void;
}

/**
 * Button with SnackBar - variant must be any of these ('success','error','info','warning')
 * Based on variant the snackbar color and icon changes
 */

const variantIcon = {
  error: Error,
  info: Info,
  success: CheckCircle,
  warning: Warning,
};

const useVariants = makeStyles(theme => ({
  error: {
    backgroundColor: theme.palette.error.dark,
  },
  info: {
    backgroundColor: theme.palette.primary.main,
  },
  success: {
    backgroundColor: green[600],
  },
  warning: {
    backgroundColor: amber[700],
  },
}));

const useStyles = makeStyles(theme => ({
  close: {
    padding: theme.spacing(0.5),
  },
  icon: {
    fontSize: 20,
  },
  iconVariant: {
    marginRight: theme.spacing(1),
    opacity: 0.9,
  },
  margin: {
    margin: theme.spacing(1),
  },
  message: {
    alignItems: 'center',
    display: 'flex',
  },
}));

function MySnackbarContentWrapper(props: MySnackbarContentWrapperProps) {
  const classes = useStyles(props);
  const variants = useVariants(props);
  const { className, message, onClose, variant, ...other } = props;
  const Icon = variantIcon[variant];
  return (
    <SnackbarContent
      className={clsx(variants[variant], className)}
      aria-describedby="client-snackbar"
      message={
        <span id="client-snackbar" className={classes.message}>
          <Icon className={clsx(classes.icon, classes.iconVariant)} />
          {message}
        </span>
      }
      action={[
        <IconButton
          key="close"
          aria-label="close"
          color="inherit"
          onClick={onClose}
        >
          <Close className={classes.icon} />
        </IconButton>,
      ]}
      {...other}
    />
  );
}

export default function ButtonWithSnackBar(props: ButtonWithSnackBarProps) {
  const {
    buttonClassName,
    buttonVariant,
    buttonSize,
    buttonIcon,
    buttonColor,
    buttonTitle,
    buttonStyle,
    buttonLabel,
    snackBarMessage,
    onSubmit,
    variant,
  } = props;

  const [open, setOpen] = React.useState(false);

  function handleClick() {
    if (!props.hideSnack) {
      setOpen(true);
    }
    onSubmit();
  }

  function handleClose() {
    setOpen(false);
  }
  return (
    <React.Fragment>
      <Button
        aria-label={buttonLabel && buttonLabel}
        className={buttonClassName}
        color={buttonColor}
        variant={buttonVariant}
        size={buttonSize}
        style={buttonStyle}
        onClick={event => {
          event.stopPropagation();
          handleClick();
        }}
      >
        {buttonIcon && buttonIcon}
        {buttonTitle && buttonTitle}
      </Button>
      <Snackbar
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <MySnackbarContentWrapper
          onClose={handleClose}
          variant={variant}
          message={snackBarMessage}
        />
      </Snackbar>
    </React.Fragment>
  );
}
